import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
} from '@mui/material';
import {
  Home as HomeIcon,
  Person as PersonIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Use MUI's Modal scroll lock and backdrop touch blocking

  const menuItems = [
    { label: 'HOME', path: '/', icon: <HomeIcon /> },
    { label: 'ABOUT', path: '/about', icon: <PersonIcon /> },
    { label: 'CONTACT', path: '/contact', icon: <EmailIcon /> },
  ];

  const HamburgerIcon = ({ open }: { open: boolean }) => (
    <Box
      sx={{
        width: 24,
        height: 24,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
        cursor: 'pointer',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: 2,
          backgroundColor: 'white',
          transformOrigin: 'center',
          transition: 'all 0.3s ease',
          transform: open ? 'rotate(45deg) translate(5px, 5px)' : 'none',
        }}
      />
      <Box
        sx={{
          width: '100%',
          height: 2,
          backgroundColor: 'white',
          transition: 'all 0.3s ease',
          opacity: open ? 0 : 1,
        }}
      />
      <Box
        sx={{
          width: '100%',
          height: 2,
          backgroundColor: 'white',
          transformOrigin: 'center',
          transition: 'all 0.3s ease',
          transform: open ? 'rotate(-45deg) translate(5px, -5px)' : 'none',
        }}
      />
    </Box>
  );

  const drawer = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
        color: 'white',
        padding: 2,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        pb: 'calc(env(safe-area-inset-bottom) + 16px)'
      }}
    >
      <List sx={{ mt: 12 }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.label}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              mb: 2,
              borderRadius: 2,
              transition: 'all 0.3s ease',
              backgroundColor: location.pathname === item.path ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(59, 130, 246, 0.3)',
                transform: 'translateX(10px)',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#3b82f6', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '1.2rem',
                fontWeight: 600,
                letterSpacing: '0.1em',
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={0} 
        sx={{ 
          backgroundColor: 'transparent',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ justifyContent: 'flex-start', px: 3, pt: 2 }}>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            disableRipple
            sx={{
              p: 2,
              mt: 1,
              backgroundColor: 'rgba(15, 23, 42, 0.8)',
              borderRadius: '50%',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                transform: 'scale(1.05)',
              },
              '&:focus': {
                backgroundColor: 'rgba(15, 23, 42, 0.8)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <HamburgerIcon open={mobileOpen} />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: false,
        }}
        BackdropProps={{ sx: { touchAction: 'none' } }}
        sx={{
          '& .MuiDrawer-paper': {
            backgroundColor: 'transparent',
            border: 'none',
            // Full-screen drawer on mobile
            // - Mobile and tablet: full width
            // - Desktop: standard drawer width
            width: { xs: '100vw', lg: 360 },
            // Always span full viewport height across devices
            height: '100dvh',
            maxHeight: '100dvh',
            // Prevent double scrollbars; let inner content scroll
            overflow: 'hidden',
            // Also force full-screen in mobile landscape
            '@media (orientation: landscape) and (max-width: 1023px)': {
              width: '100vw',
              height: '100dvh',
            },
          },
        }}
      >
        <Box sx={{ height: '100%', width: '100%' }}>
          {drawer}
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
